import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useCallback } from 'react'
import SimplePageHeader2 from '@/components/SimplePageHeader2'
import {
  useSubscribeTeam,
  useGQL_teamsSimple,
  useGQL_teamSubscriptionStatus,
  useGQL_subscriptionPlans,
} from '@/contexts/graphql-and-swr-apis';
import { SimpleButton, PageContentWrapper } from '@unwrittenmedia/simple-components'
import { createAuthGuard } from '@/lib/authGuard'


const cxStyles = {
  // Container & Layout
  container: 'mt-8 max-w-2xl mx-auto',
  cardWrapper: 'bg-white dark:bg-mist-900 shadow rounded-lg ring-1 ring-gray-200 dark:ring-mist-800 overflow-hidden',
  contentSection: 'px-4 py-5 sm:p-6',
  bottomSection: 'px-4 py-4 sm:px-6 bg-gray-50 dark:bg-mist-950/50',
  divider: 'border-t border-gray-200 dark:border-mist-800',

  // Typography - Labels & Text
  label: 'block text-sm font-medium text-gray-900 dark:text-white',
  labelWithSpacing: 'block text-sm font-medium text-gray-900 dark:text-white mb-2',
  secondaryText: 'text-sm text-gray-500 dark:text-mist-500',
  planTitle: 'text-sm font-semibold text-gray-900 dark:text-white',
  planPrice: 'text-2xl font-bold text-gray-900 dark:text-white',
  planInterval: 'text-base font-normal text-gray-500 dark:text-mist-500',

  // Form Inputs
  input: 'block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-mist-700 placeholder:text-gray-400 dark:placeholder:text-mist-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm/6 bg-white dark:bg-mist-950',
  select: 'block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-mist-700 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm/6 bg-white dark:bg-mist-950',
  inputWrapper: 'mt-2',

  // Form Sections
  billingPlanSection: 'mt-6',
  planButton: (selected: boolean) =>
    `w-full flex items-center justify-between px-4 py-3 border-2 rounded-lg transition-colors ${
      selected
        ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
        : 'border-gray-300 dark:border-mist-700 hover:border-indigo-500 dark:hover:border-indigo-400'
    }`,
  planInfo: 'flex flex-col items-start',

  // Error Styling
  errorContainer: 'mt-6 rounded-md bg-red-50 dark:bg-red-900/20 p-4',
  errorText: 'text-sm text-red-800 dark:text-red-400',
}

type AddTeamSearch = {
  teamId?: string
  planId?: string
  screenCount?: number
}

export const Route = createFileRoute('/team/add-team')({
  beforeLoad: createAuthGuard(),
  component: AddTeamPage,
  validateSearch: (search: Record<string, unknown>): AddTeamSearch => ({
    teamId: (search.teamId as string) || undefined,
    planId: (search.planId as string) || undefined,
    screenCount: search.screenCount !== undefined ? Number(search.screenCount) : undefined,
  }),
})

const ADD_NEW_TEAM_VALUE = '__add_new__';

function AddTeamPage() {
  const { teamId: preselectedTeamId, planId: preselectedPlanId, screenCount: preselectedScreenCount } = Route.useSearch()
  const { mutateAsync, isPending: isLoading, isError, sfErrors } = useSubscribeTeam();
  const navigate = useNavigate()
  const [teamName, setTeamName] = useState('')
  const { teams: existingTeams, isLoading: teamsLoading } = useGQL_teamsSimple({ withoutSubscription: true });
  const { data: plansData } = useGQL_subscriptionPlans();
  const planOptions = plansData?.subscriptionPlans ?? [];

  // Existing-team picker
  const hasExistingTeams = existingTeams.length > 0;
  const [selectedTeamId, setSelectedTeamId] = useState<string>(preselectedTeamId || ADD_NEW_TEAM_VALUE);
  const isCreatingNew = selectedTeamId === ADD_NEW_TEAM_VALUE;

  // Subscription options — default to the first plan returned by the server
  const [selectedLevel, setSelectedLevel] = useState<string>(preselectedPlanId || '');
  const effectiveLevel = selectedLevel || planOptions[0]?.id || '';
  const [screenCount, setScreenCount] = useState(preselectedScreenCount ?? 1);

  // Pending team ID — set after the mutation returns; drives the polling state
  const [pendingTeamId, setPendingTeamId] = useState<string | null>(null);
  const { isSyncing } = useGQL_teamSubscriptionStatus(pendingTeamId, 2000);

  // Redirect once subscription sync is confirmed
  useEffect(() => {
    if (pendingTeamId && !isSyncing) {
      navigate({ to: '/team/$teamId/manage', params: { teamId: pendingTeamId } });
    }
  }, [pendingTeamId, isSyncing, navigate]);

  const handleTeamSelect = (value: string) => {
    setSelectedTeamId(value);
  };

  const handleSubscribe = useCallback(async () => {
    try {
      const input = isCreatingNew
        ? { teamName: teamName.trim(), level: effectiveLevel, screenCount }
        : { teamId: selectedTeamId, level: effectiveLevel, screenCount };

      const result = await mutateAsync(input);
      setPendingTeamId(result.subscribeTeam.id);
    } catch (_) {
      // error surfaced via isError / error fields
    }
  }, [isCreatingNew, teamName, effectiveLevel, screenCount, selectedTeamId, mutateAsync]);

  const handleUnsubscribe = useCallback(async () => {
    const confirmed = window.confirm('Unsubscribe immediately? Contact support for more options');
    if (!confirmed) return;
    try {
      await mutateAsync({ teamId: selectedTeamId, level: 'Unsubscribe', screenCount: 0 });
      navigate({ to: '/today' });
    } catch (_) {
      // error surfaced via sfErrors
    }
  }, [selectedTeamId, mutateAsync, navigate]);

  const createButtonText = isCreatingNew ? (isLoading ? 'Creating...' : 'Create Team') : (isLoading ? 'Subscribing...' : 'Subscribe');

  // ── Polling / confirming state ────────────────────────────────────────────
  if (pendingTeamId) {
    return (
      <PageContentWrapper>
        <div className="mt-8 max-w-2xl mx-auto flex flex-col items-center gap-4 py-16">
          <svg
            className="animate-spin h-8 w-8 text-indigo-600 dark:text-indigo-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <p className="text-sm text-gray-600 dark:text-mist-500">Setting up subscription…</p>
        </div>
      </PageContentWrapper>
    );
  }

  const canSubmit = isCreatingNew ? teamName.trim().length > 0 : true;

  return (
    <PageContentWrapper>
      <SimplePageHeader2
        title="Add Team"
        description="A team holds all of your screens, and controls who can manage them."
        actionButtonText="Create"
        actionButtonOnClick={handleSubscribe}
      />

      <div className={cxStyles.container}>
        <div className={cxStyles.cardWrapper}>
          <div className={cxStyles.contentSection}>
            
            {/* Existing-team dropdown — hidden when a team is pre-selected via URL */}
            {!preselectedTeamId && !teamsLoading && hasExistingTeams && (
              <div>
                <label htmlFor="team-select" className={cxStyles.labelWithSpacing}>
                  Select Team
                </label>
                <div className={cxStyles.inputWrapper}>
                  <select
                    id="team-select"
                    value={selectedTeamId}
                    onChange={(e) => handleTeamSelect(e.target.value)}
                    className={cxStyles.select}
                  >
                    {existingTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                    <option value={ADD_NEW_TEAM_VALUE}>Add new team…</option>
                  </select>
                </div>
              </div>
            )}

            {/* New team name field */}
            {isCreatingNew && (
              <div className={hasExistingTeams ? 'mt-6' : undefined}>
                <label htmlFor="team-name" className={cxStyles.label}>
                  New Team Name
                </label>
                <div className={cxStyles.inputWrapper}>
                  <input
                    type="text"
                    name="team-name"
                    id="team-name"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Enter team name"
                    className={cxStyles.input}
                  />
                </div>
              </div>
            )}

            {/* Subscription level */}
            <div className={cxStyles.billingPlanSection}>
              <label className={cxStyles.labelWithSpacing}>Subscription Plan</label>
              <div className="space-y-2">
                {planOptions.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedLevel(plan.id)}
                    className={cxStyles.planButton(effectiveLevel === plan.id)}
                  >
                    <div className={cxStyles.planInfo}>
                      <span className={cxStyles.planTitle}>{plan.name}</span>
                      {plan.description && <span className={cxStyles.secondaryText}>{plan.description}</span>}
                    </div>
                    <span className={cxStyles.planPrice}>
                      $0<span className={cxStyles.planInterval}>/mo</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Screen count */}
            <div className="mt-6">
              <label htmlFor="screen-count" className={cxStyles.label}>
                Number of Screens
              </label>
              <div className={cxStyles.inputWrapper}>
                <input
                  type="number"
                  id="screen-count"
                  name="screen-count"
                  min={1}
                  value={screenCount}
                  onChange={(e) => setScreenCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className={`${cxStyles.input} max-w-xs`}
                />
              </div>
            </div>

            {/* Error messages */}
            {(sfErrors.length > 0) && (
              <div className={cxStyles.errorContainer}>
                {sfErrors?.map((err, index) => (
                  <div key={index} className={cxStyles.errorText}>
                    {err.message || "*Unknown error*"}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={cxStyles.divider} />

          

          <div className={cxStyles.bottomSection}>
              <SimpleButton
                onClick={handleSubscribe}
                disabled={isLoading || !canSubmit}
                className="w-full"
                color="primary"
                size="md"
              >
                {createButtonText}
              </SimpleButton>
              {!isCreatingNew && (
                <SimpleButton
                  onClick={handleUnsubscribe}
                  disabled={isLoading}
                  className="w-full mt-2"
                  color="danger"
                  size="md"
                >
                  Unsubscribe
                </SimpleButton>
              )}
          </div>
        </div>
      </div>
    </PageContentWrapper>
  )
}
